// Hero hotspots sit at the merchant's x/y on the photo (ns-hero.css maps them through object-fit: cover). A dot whose product is cropped out of the hero, or that lands on the copy, is hidden.

const RING = 8;
const FADE = 950;

// The slide after `at` moving `step`, wrapping round.
export const nextSlide = (at, step, count) => (at + step + count) % count;

const touches = (a, b, ring) => a.left - ring < b.right && b.left < a.right + ring && a.top - ring < b.bottom && b.top < a.bottom + ring;

const outside = (dot, frame) => {
  const x = (dot.left + dot.right) / 2;
  const y = (dot.top + dot.bottom) / 2;
  return x < frame.left || x > frame.right || y < frame.top || y > frame.bottom;
};

export function hiddenSpots(dots, copy, frame = null, ring = RING) {
  return dots.map(dot => (frame !== null && outside(dot, frame)) || copy.some(piece => touches(dot, piece, ring)));
}

const lineBoxes = element => {
  const range = document.createRange();
  range.selectNodeContents(element);
  return [...range.getClientRects()];
};

if (typeof customElements !== 'undefined' && !customElements.get('ns-hero')) {
  customElements.define('ns-hero', class extends HTMLElement {
    connectedCallback() {
      this.observer = new ResizeObserver(() => this.place());
      this.observer.observe(this);
      document.fonts?.ready.then(() => this.place());
      this.start();
    }
    disconnectedCallback() { this.observer?.disconnect(); this.stop?.(); }
    // The slideshow (sections/ns-hero.liquid): crossfade one slide over the last, every data-interval ms while the pointer, focus and
    // the tab leave it alone and the visitor has not asked for less motion. The other photos are shown (so they load) after the page has.
    start() {
      const slides = [...this.querySelectorAll('[data-slide]')];
      if (slides.length < 2) return;
      const unders = [...this.parentElement.querySelectorAll('[data-under]')];
      const dots = [...this.querySelectorAll('[data-hero-go]')];
      const pause = this.querySelector('[data-hero-pause]');
      const calm = matchMedia('(prefers-reduced-motion: reduce)');
      const interval = Number(this.dataset.interval) || 6000;
      let at = 0;
      let timer = 0;
      let held = false;
      let off = false;
      let inView = true;
      let ready = false;
      const loaded = slide => { const img = slide.querySelector('img'); return img.complete && img.naturalWidth > 0; };
      const playing = () => ready && this.hasAttribute('data-autoplay') && !calm.matches && !off && !held && inView && !document.hidden;
      const arm = () => {
        clearTimeout(timer);
        this.toggleAttribute('data-paused', !playing());
        this.setAttribute('aria-live', playing() ? 'off' : 'polite');
        if (playing()) timer = setTimeout(() => { if (loaded(slides[nextSlide(at, 1, slides.length)])) go(at + 1); else arm(); }, interval);
      };
      const go = to => {
        to = nextSlide(to, 0, slides.length);
        if (to === at) return;
        const old = slides[at];
        this.querySelector('[data-prev]')?.removeAttribute('data-prev');
        old.removeAttribute('data-on'); old.setAttribute('data-prev', ''); old.inert = true;
        slides[to].removeAttribute('data-idle'); slides[to].inert = false; slides[to].setAttribute('data-on', '');
        unders[at]?.removeAttribute('data-on'); unders[to]?.setAttribute('data-on', '');
        dots[at]?.removeAttribute('aria-current'); dots[to]?.setAttribute('aria-current', 'true');
        at = to;
        setTimeout(() => old.removeAttribute('data-prev'), FADE);
        requestAnimationFrame(() => this.place());
        arm();
      };
      let next = 1;
      const reveal = () => {
        const slide = slides[next++];
        if (!slide) return;
        slide.removeAttribute('data-idle');
        const img = slide.querySelector('img');
        if (loaded(slide)) reveal(); else { img.addEventListener('load', reveal, { once: true }); img.addEventListener('error', reveal, { once: true }); }
      };
      // The first slide gets its whole interval after the page has loaded, not after the script ran.
      const settle = () => { ready = true; arm(); (window.requestIdleCallback ?? setTimeout)(reveal); };
      if (document.readyState === 'complete') settle(); else addEventListener('load', settle, { once: true });
      const hold = value => event => { if (event.pointerType === undefined || event.pointerType === 'mouse') { held = value; arm(); } };
      this.addEventListener('pointerenter', hold(true));
      this.addEventListener('pointerleave', hold(false));
      this.addEventListener('focusin', () => { held = true; arm(); });
      this.addEventListener('focusout', () => { held = false; arm(); });
      this.addEventListener('click', event => {
        const step = event.target.closest('[data-hero-step]');
        const dot = event.target.closest('[data-hero-go]');
        if (step) go(at + Number(step.dataset.heroStep));
        else if (dot) go(Number(dot.dataset.heroGo));
        else if (event.target.closest('[data-hero-pause]')) {
          off = !off;
          pause.setAttribute('aria-pressed', String(off));
          pause.setAttribute('aria-label', pause.dataset[off ? 'playLabel' : 'pauseLabel']);
          arm();
        }
      });
      this.addEventListener('keydown', event => {
        if ((event.key === 'ArrowLeft' || event.key === 'ArrowRight') && event.target.closest('.ns-hero__nav')) go(at + (event.key === 'ArrowRight' ? 1 : -1));
      });
      let x = 0;
      let y = 0;
      this.addEventListener('touchstart', event => { x = event.touches[0].clientX; y = event.touches[0].clientY; }, { passive: true });
      this.addEventListener('touchend', event => {
        const dx = event.changedTouches[0].clientX - x;
        if (Math.abs(dx) > 48 && Math.abs(dx) > 1.5 * Math.abs(event.changedTouches[0].clientY - y)) go(at + (dx < 0 ? 1 : -1));
      }, { passive: true });
      const watch = new IntersectionObserver(([entry]) => { inView = entry.isIntersecting; arm(); }, { threshold: 0.3 });
      watch.observe(this);
      document.addEventListener('visibilitychange', arm);
      calm.addEventListener('change', arm);
      this.stop = () => { clearTimeout(timer); watch.disconnect(); document.removeEventListener('visibilitychange', arm); };
      arm();
    }
    place() {
      const spots = [...this.querySelectorAll('.ns-hotspot')];
      if (spots.length === 0) return;
      const copy = [
        ...[...this.querySelectorAll('.ns-hero__title, .ns-hero__text')].flatMap(lineBoxes),
        ...[...this.querySelectorAll('.ns-hero__eyebrow, .ns-hero__ctas > *, .ns-hero__trust > li, .ns-hero__look > *, .ns-hero-pop')].map(element => element.getBoundingClientRect()),
      ].filter(box => box.width > 0 && box.height > 0);
      for (const spot of spots) spot.hidden = false;
      const hide = hiddenSpots(spots.map(spot => spot.querySelector('.ns-hotspot__dot').getBoundingClientRect()), copy, this.getBoundingClientRect());
      spots.forEach((spot, index) => { spot.hidden = hide[index]; });
    }
  });
}
